import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import countryList from "react-select-country-list";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChanges";

import {
  savePlant,
  editPlant,
  getPlantDetails,
  getRONFElements,
  getPlantUtilityCosts,
  updatePlantChemicalLibrary,
  updatePlantUtilityCosts,
} from "../../network/cipAPI";
import {
  storeUnits,
  storePlantUtilityCosts,
  storePlantDetails,
  updateSelectedTrain,
} from "../../Store/TemplateStore/template.action";

import Header from "../../components/Header";
import ErrorLabel from "../../components/ErrorLabel";
import InputBox from "../../components/InputBox";
import Dropdown from "../../components/Dropdown";
import DupontButton from "../../components/DupontButton";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import Settings from "../home/Settings";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";
import {
  getChangedData,
  updatePlantId,
  getSelectedPlantTrainInfo,
} from "../../utils/Utils";
import {
  getItemSession,
  removeItemSession,
  saveItemSession,
  setLocalStorageItem,
} from "../../utils/session";
import { WATER_TYPE } from "./addNewPlant.config";
import RoConfiguration from "./roConfiguration";
import { API_URLS } from "../../network/appUrls";
import Footer from "../../common/footer/Footer";
import CommentsOnSpecialElements from "../../components/CommentsOnSpecialElements";
import ChemicalsUsedForCleaning from "../../components/ChemicalsUsedForCleaning";
import {
  PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
  PENDING_PLANT_UTILITY_COSTS_KEY,
  PENDING_PLANT_BASELINE_UNITS_KEY,
} from "../../constants/global.constants";
import { NodeExtensionIcon } from "../../assets/icons";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import ImportTrainConfigModal from "./ImportTrainConfig";
import { getInitialChemicalSelection } from "../../components/ChemicalSelectionFields/utils";
import { convertUtilityFormValues } from "../../components/UtilityCosts/utilityCosts.config";
import {
  toStoreUtilityCosts,
  toUtilityApiPayload,
  toUtilityFormValues,
} from "../../components/UtilityCosts/UtilityCostsDialog/UtilityCostsDialog.api";

import "./index.scss";
import CircularLoader from "../../common/styles/components/circularLoader";

const basePath = API_URLS.basePath;

const AddNewPlant = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    token,
    flow,
    pressure,
    feed_quality,
    feed_water_factor,
    permeate_quality,
    feed_water_permeate_factor,
    temperature,
    flux,
    volume,
    mass,
    currency,
    conversion_rate,
    plantData,
    accessUserPlant,
    plantChemicalLibrary,
    plantUtilityCosts,
  } = useSelector((state) => state);

  const [noOfTrains, setNoOfTrains] = useState(location.state?.noOfTrains);
  const [waterTypeOptions, setWaterTypeOptions] = useState(WATER_TYPE(t));
  const [plantDetail, setPlantDetail] = useState([]);
  const [plantDetailerror, setPlantDetailError] = useState([]);
  const [country, setCountry] = useState("");
  const [waterType, setWaterType] = useState("");
  const [trains, setTrains] = useState([]);
  const [totalNumberOfElements, setTotalNumberOfElements] = useState("");
  const [stageValidationSucessfull, setStageValidationSucessfull] =
    useState(true);
  const [fetchedPlanDetails, setFetchedPlanDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [createdPlantId, setCreatedPlantId] = useState(null);
  const [RONFElements, setRONFElements] = useState([]);
  const [chemicalSelection, setChemicalSelection] = useState({});
  const [isTrainConfigModalOpen, setIsTrainConfigModalOpen] = useState(false);
  const [mandatoryComponentsStatus, setMandatoryComponentsStatus] = useState({
    plantInformation: {
      fulfilled: false,
      changed: false,
      error: false,
    },
    roConfiguration: {
      fulfilled: false,
      changed: false,
      error: false,
    },
    settings: {
      fulfilled: true, // Always fulfilled - radio buttons always have valid values
      changed: false,
      error: false,
    },
    chemicalsUsedForCleaning: {
      fulfilled: true,
      changed: false,
      error: false,
    },
  });

  const [baselineUnits, setBaselineUnits] = useState(() => ({
    currency: currency || "USD",
    volume: volume || "m³",
    conversion_rate: Number(conversion_rate) || 1,
  }));

  // initialize chemical selection when plant chemical library is loaded & chemical selection is empty
  // or update chemical selection prices when plant chemical library changes
  useEffect(() => {
    if (!plantChemicalLibrary?.length) {
      return;
    }
    if (!chemicalSelection || Object.keys(chemicalSelection).length === 0) {
      const initialSelection =
        getInitialChemicalSelection(plantChemicalLibrary);
      setChemicalSelection(initialSelection);
    } else {
      const updatedSelection = { ...chemicalSelection };
      Object.keys(chemicalSelection).forEach((categoryKey) => {
        const chemId = chemicalSelection[categoryKey]?.chem_id;
        const plantChem = plantChemicalLibrary.find(
          (chem) => chem.chem_id === chemId,
        );
        if (plantChem) {
          updatedSelection[categoryKey] = {
            ...chemicalSelection[categoryKey],
            price: plantChem.price,
          };
        }
      });
      setChemicalSelection(updatedSelection);
    }
  }, [plantChemicalLibrary]);

  useEffect(() => {
    saveItemSession(
      PENDING_PLANT_BASELINE_UNITS_KEY,
      JSON.stringify(baselineUnits),
    );
  }, [baselineUnits]);

  useEffect(() => {
    return () => {
      removeItemSession(PENDING_PLANT_BASELINE_UNITS_KEY);
    };
  }, []);

  const countryOptions = useMemo(
    () =>
      countryList()
        .getData()
        .map((item) => item.label),
    [],
  );

  const searchParams = new URLSearchParams(location.search);
  const isEdit = searchParams.get("isEdit");
  const plantId = searchParams.get("plantId");

  const storeInitialConfig = (trains) => {
    if (!trains) return;
    const result = {};
    Object.values(trains).forEach((train) => {
      const trainId = train.train_id;
      result[trainId] = {};
      train.passes.forEach((pass) => {
        const passId = pass.pass_id;
        result[trainId][passId] = {};
        pass.stages.forEach((stage) => {
          const stageId = stage.stage_id;
          const numElements =
            Number(stage.num_pressure_vessels) *
            Number(stage.num_element_per_pressure_vessel);
          result[trainId][passId][stageId] = numElements;
        });
      });
    });
    setLocalStorageItem("prevTrainConfig", JSON.stringify(result));
  };

  function normalizeRoNfElementUsed(trains) {
    const newTrains = JSON.parse(JSON.stringify(trains));
    for (const train of Object.values(newTrains)) {
      for (const pass of train.passes) {
        for (const stage of pass.stages) {
          if (stage.is_isd) {
            if (!Array.isArray(stage.ro_nf_element_used)) {
              stage.ro_nf_element_used = stage.ro_nf_element_used
                ? [stage.ro_nf_element_used]
                : [];
            }
          } else {
            if (Array.isArray(stage.ro_nf_element_used)) {
              stage.ro_nf_element_used = stage.ro_nf_element_used[0] || "";
            } else if (!stage.ro_nf_element_used) {
              stage.ro_nf_element_used = "";
            }
          }
        }
      }
    }
    return newTrains;
  }

  const selectedElementsCommentsData = useMemo(() => {
    if (!trains || Object.keys(trains).length === 0 || !RONFElements?.length) {
      return [];
    }

    // Collect all unique elements from DuPont trains
    const uniqueElements = new Set();
    Object.values(trains).forEach((train) => {
      if (!train.is_dupont_element) return;
      train.passes?.forEach((pass) => {
        pass.stages?.forEach((stage) => {
          const used = stage.ro_nf_element_used;
          if (!used) return;

          if (Array.isArray(used)) {
            used.forEach((el) => el?.trim() && uniqueElements.add(el));
          } else if (typeof used === "string" && used.trim()) {
            uniqueElements.add(used);
          }
        });
      });
    });

    // Group elements by their comments
    const commentsMap = new Map();
    uniqueElements.forEach((element) => {
      const elementObj = RONFElements.find(
        (item) => item.id === element && item.comment,
      );
      if (elementObj) {
        const { comment, id } = elementObj;
        if (!commentsMap.has(comment)) {
          commentsMap.set(comment, []);
        }
        commentsMap.get(comment).push(id);
      }
    });

    return Array.from(commentsMap, ([comment, elements]) => ({
      elements,
      comment,
    }));
  }, [trains, RONFElements]);

  const normalizeVolumeUnit = (unit) => {
    const normalized = String(unit || "").toLowerCase();

    if (normalized === "m3" || normalized === "m³") {
      return "m3";
    }

    return normalized;
  };

  const hasUnitChangesForUtilityCosts = () => {
    const previousCurrency = baselineUnits?.currency || "USD";
    const previousVolume = normalizeVolumeUnit(baselineUnits?.volume || "m³");
    const previousRate = Number(baselineUnits?.conversion_rate) || 1;

    const nextCurrency = currency || "USD";
    const nextVolume = normalizeVolumeUnit(volume || "m³");
    const nextRate = Number(conversion_rate) || 1;

    return (
      previousCurrency !== nextCurrency ||
      previousVolume !== nextVolume ||
      previousRate !== nextRate
    );
  };

  const hasCompleteUtilityCosts = (apiValues = {}) => {
    const formValues = toUtilityFormValues(apiValues);

    return ["priceOfWater", "costOfDowntime", "costOfElectricity"].every(
      (fieldKey) => {
        const rawValue = formValues[fieldKey];
        if (rawValue === null || rawValue === undefined || rawValue === "") {
          return false;
        }

        const parsedValue = Number(rawValue);
        return Number.isFinite(parsedValue);
      },
    );
  };

  const isAllZeroUtilityCosts = (apiValues = {}) => {
    if (!hasCompleteUtilityCosts(apiValues)) {
      return false;
    }

    const payload = toUtilityApiPayload(toUtilityFormValues(apiValues));
    return (
      payload.water_price === 0 &&
      payload.downtime_cost === 0 &&
      payload.energy_price === 0
    );
  };

  useEffect(() => {
    if (waterType === "") {
      return;
    }
    setRONFElements([]);
    let options = [];

    const fetchRONFElements = async () => {
      try {
        const response = await getRONFElements(waterType);
        if (response && response.data) {
          const formattedData = response.data.map((element) => {
            return {
              id: element.name,
              label: element.name,
              value: element.name,
              comment: element.comment,
            };
          });
          options = formattedData;
        }
      } catch (error) {
        createNotification(
          ERROR_NOTIFICATION,
          t("ValueStrings.somethingWentWrong"),
          "",
        );
      } finally {
        setRONFElements(options);
      }
    };

    fetchRONFElements();
  }, [waterType]);

  useEffect(() => {
    setWaterTypeOptions(WATER_TYPE(t));
  }, [t]);

  useEffect(() => {
    if (token && isEdit && plantId) {
      const getPlantDetail = async () => {
        setIsLoading(true);
        try {
          // Existing-plant edit flow should not reuse pending pre-create utility payloads.
          removeItemSession(PENDING_PLANT_UTILITY_COSTS_KEY);

          const sel_train = getSelectedPlantTrainInfo(plantData, plantId);
          const response = await getPlantDetails(plantId, false, sel_train);
          if (response && response.data) {
            const {
              name,
              description,
              city,
              country,
              num_trains,
              water_type,
              config,
              setting,
              utility_costs,
              chemical_selection,
            } = response.data;

            dispatch(updateSelectedTrain(sel_train));
            setFetchedPlanDetails(response.data);
            setPlantDetail({
              ...plantDetail,
              installationName: name,
              description,
              city,
            });
            setCountry(country);
            setNoOfTrains(num_trains);
            setWaterType(water_type);
            dispatch(
              storePlantDetails({
                plantName: name,
                water_type,
              }),
            );
            dispatch(storeUnits(setting));
            setBaselineUnits({
              currency: setting?.currency || "USD",
              volume: setting?.volume || "m³",
              conversion_rate: Number(setting?.conversion_rate) || 1,
            });

            // Prefer dedicated utility-cost endpoint when available, fallback to plant details.
            let resolvedUtilityCosts = null;
            try {
              const utilityResponse = await getPlantUtilityCosts();
              if (utilityResponse?.status === 200) {
                resolvedUtilityCosts = utilityResponse?.data || null;
              }
            } catch (utilityError) {
              resolvedUtilityCosts = null;
            }

            const utilitySourceForStore = hasCompleteUtilityCosts(
              resolvedUtilityCosts,
            )
              ? resolvedUtilityCosts
              : utility_costs;

            if (hasCompleteUtilityCosts(utilitySourceForStore)) {
              dispatch(
                storePlantUtilityCosts(
                  toStoreUtilityCosts(utilitySourceForStore),
                ),
              );
            }
            setTrains(config?.trains);
            storeInitialConfig(config?.trains);
            setChemicalSelection(chemical_selection);
          }
        } catch (error) {
          setIsLoading(false);
        } finally {
          setIsLoading(false);
        }
      };
      getPlantDetail();
    }
  }, [token]);

  // Helper to update a single section's status in mandatoryComponentsStatus
  const updateComponentStatus = (section, updates) => {
    setMandatoryComponentsStatus((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...updates },
    }));
  };

  const changeCountry = (value) => {
    setCountry(value);
    setPlantDetailError({ ...plantDetailerror, country: "" });
    updateComponentStatus("plantInformation", { changed: true });
  };
  const changeWaterType = (value) => {
    setWaterType(value);
    setPlantDetailError({ ...plantDetailerror, waterType: "" });
    updateComponentStatus("plantInformation", { changed: true });
  };
  const handleInputChange = (key, value) => {
    setPlantDetail({ ...plantDetail, [key]: value });
    if (key === "installationName") {
      const error =
        value.trim() === "" ? t("addNewPlant.installationNameRequired") : "";
      setPlantDetailError({ ...plantDetailerror, [key]: error });
    } else {
      setPlantDetailError({ ...plantDetailerror, [key]: "" });
    }
    updateComponentStatus("plantInformation", { changed: true });
  };

  const runValidation = () => {
    setStageValidationSucessfull(false);
    let validationSuccessful = true;
    let plantInfoValid = true;
    let roConfigValid = true;
    const error = [];
    // Validate Plant Information
    if (!plantDetail.installationName) {
      validationSuccessful = false;
      plantInfoValid = false;
      error["installationName"] = t("addNewPlant.installationNameRequired");
    }
    if (!country) {
      validationSuccessful = false;
      plantInfoValid = false;
      error["country"] = t("addNewPlant.countryRequired");
    }
    if (!waterType) {
      validationSuccessful = false;
      plantInfoValid = false;
      error["waterType"] = t("addNewPlant.waterTypeRequired");
    }

    // Update plant information status
    updateComponentStatus("plantInformation", {
      fulfilled: plantInfoValid,
      changed: false,
      error: !plantInfoValid,
    });

    setPlantDetailError({
      ...plantDetailerror,
      ...error,
    });
    // Validate RO Configuration
    for (const train of Object.values(trains)) {
      for (const pass of train.passes) {
        if (pass?.stages?.length === 0) {
          validationSuccessful = false;
          roConfigValid = false;
          break;
        }
        for (const stage of pass.stages) {
          // Check for ISD: must be non-empty array
          if (stage.is_isd) {
            if (
              !Array.isArray(stage.ro_nf_element_used) ||
              stage.ro_nf_element_used.length === 0
            ) {
              validationSuccessful = false;
              roConfigValid = false;
              break;
            }
          } else {
            // Single: must be non-empty string
            if (
              !stage.ro_nf_element_used ||
              (typeof stage.ro_nf_element_used === "string" &&
                stage.ro_nf_element_used.trim() === "")
            ) {
              validationSuccessful = false;
              roConfigValid = false;
              break;
            }
          }
          if (
            !stage.num_pressure_vessels ||
            !stage.num_element_per_pressure_vessel
          ) {
            validationSuccessful = false;
            roConfigValid = false;
            break;
          }
        }
      }
    }

    // Update RO configuration status
    updateComponentStatus("roConfiguration", {
      fulfilled: roConfigValid,
      changed: false,
      error: !roConfigValid,
    });

    return validationSuccessful;
  };

  const savePlantDetails = async (key, opts = {}) => {
    if (!opts.skipLoading) setIsLoading(true);
    setIsSaving(true);
    try {
      const stringifyCostValues = (cost = {}) => {
        Object.keys(cost).forEach((costKey) => {
          cost[costKey] = String(cost[costKey]);
        });
      };

      const syncPendingPayload = async (
        sessionKey,
        syncApi,
        failedServiceName,
        failedServices,
      ) => {
        const pendingPayload = getItemSession(sessionKey);
        if (!pendingPayload) {
          return;
        }

        try {
          const syncResponse = await syncApi(JSON.parse(pendingPayload));
          if (syncResponse?.status !== 200) {
            throw new Error(
              `${failedServiceName} sync failed with status ${syncResponse?.status}`,
            );
          }

          removeItemSession(sessionKey);
        } catch (syncError) {
          failedServices.push(failedServiceName);
        }
      };

      const syncConvertedUtilityCostsIfNeeded = async (failedServices) => {
        if (!hasUnitChangesForUtilityCosts()) {
          return false;
        }

        const utilityServiceName = t("plantLevel.utilityCosts");

        try {
          const pendingUtilityCosts = getItemSession(
            PENDING_PLANT_UTILITY_COSTS_KEY,
          );

          let sourceUtilityCosts = null;

          if (pendingUtilityCosts) {
            const pendingSource = JSON.parse(pendingUtilityCosts);
            if (hasCompleteUtilityCosts(pendingSource)) {
              sourceUtilityCosts = pendingSource;
            }
          } else if (
            plantUtilityCosts?.water_price !== undefined ||
            plantUtilityCosts?.energy_price !== undefined ||
            plantUtilityCosts?.downtime_cost !== undefined
          ) {
            sourceUtilityCosts = plantUtilityCosts;
          }

          const shouldRefreshFromApi =
            !sourceUtilityCosts || isAllZeroUtilityCosts(sourceUtilityCosts);

          if (shouldRefreshFromApi) {
            // Last-resort fallback for flows where utility values were never loaded into store.
            const utilityResponse = await getPlantUtilityCosts();
            if (utilityResponse?.status === 200) {
              const fetchedUtilityCosts = utilityResponse?.data || {};

              if (hasCompleteUtilityCosts(fetchedUtilityCosts)) {
                sourceUtilityCosts = fetchedUtilityCosts;
                dispatch(
                  storePlantUtilityCosts(
                    toStoreUtilityCosts(fetchedUtilityCosts),
                  ),
                );
              }
            }
          }

          if (!sourceUtilityCosts) {
            return false;
          }

          if (!hasCompleteUtilityCosts(sourceUtilityCosts)) {
            throw new Error("Incomplete utility costs source for conversion");
          }

          const convertedFormValues = convertUtilityFormValues(
            toUtilityFormValues(sourceUtilityCosts),
            {
              currency: baselineUnits?.currency,
              volume: baselineUnits?.volume,
              conversion_rate: baselineUnits?.conversion_rate,
            },
            {
              currency,
              volume,
              conversion_rate,
            },
          );

          const convertedPayload = toUtilityApiPayload(convertedFormValues);
          const utilityUpdateResponse =
            await updatePlantUtilityCosts(convertedPayload);

          if (utilityUpdateResponse?.status !== 200) {
            throw new Error(
              `Utility cost conversion sync failed with status ${utilityUpdateResponse?.status}`,
            );
          }

          dispatch(storePlantUtilityCosts(convertedPayload));
          removeItemSession(PENDING_PLANT_UTILITY_COSTS_KEY);
          return true;
        } catch (syncError) {
          failedServices.push(utilityServiceName);
          return true;
        }
      };

      const validated = runValidation();
      setStageValidationSucessfull(validated);
      let response = null;
      if (validated) {
        const activePlantId = plantId || createdPlantId;
        // Normalize ro_nf_element_used for all stages before building payload
        const normalizedTrains = normalizeRoNfElementUsed(trains);
        const payload = {
          name: plantDetail.installationName,
          description: plantDetail.description,
          city: plantDetail.city,
          country: country,
          water_type: waterType,
          setting: {
            flow: flow,
            pressure: pressure,
            feed_quality: feed_quality,
            feed_water_factor: feed_water_factor,
            permeate_quality: permeate_quality,
            feed_water_permeate_factor: feed_water_permeate_factor,
            temperature: temperature,
            flux: flux,
            volume: volume,
            mass: mass,
            currency: currency,
            conversion_rate: conversion_rate,
          },
          config: {
            trains: [...Object.values(normalizedTrains)],
          },
          tol_num_elements: totalNumberOfElements,
        };

        saveItemSession("water_type", waterType);
        saveItemSession("plantName", plantDetail.installationName);
        // Edit flow with existing plantId
        if (plantId && isEdit === "true") {
          const editedFields = getChangedData(payload, fetchedPlanDetails);
          // For changed chem_id, send full item (chem_id, chemical_name, price).
          const changedChemicalSelection = {};
          Object.keys(chemicalSelection).forEach((chemicalCategory) => {
            const updatedChemical = chemicalSelection?.[chemicalCategory];
            const previousChemical =
              fetchedPlanDetails?.chemical_selection?.[chemicalCategory];

            const updatedChemId = Number(updatedChemical?.chem_id ?? "");
            const previousChemId = Number(previousChemical?.chem_id ?? "");

            if (updatedChemId !== previousChemId) {
              changedChemicalSelection[chemicalCategory] = {
                chem_id: updatedChemical?.chem_id,
                chemical_name: updatedChemical?.chemical_name,
                price: updatedChemical?.price,
              };
            }
          });
          if (Object.keys(changedChemicalSelection).length > 0) {
            editedFields.chemical_selection = changedChemicalSelection;
          }
          // Convert cost values to strings for API if they exist in editedFields
          if (editedFields.cost) {
            stringifyCostValues(editedFields.cost);
          }
          response = await editPlant(plantId, editedFields);
        }
        // Case: savePlant succeeded, but post-create chemical lib or utility cost sync failed.
        // User will not be navigated away. On retry, we have to use created plant ID to avoid duplicate plant creation.
        else if (activePlantId) {
          if (payload.cost) {
            stringifyCostValues(payload.cost);
          }
          response = await editPlant(activePlantId, payload);
        } // new plant creation flow
        else {
          // Convert cost values to strings for API
          if (payload.cost) {
            stringifyCostValues(payload.cost);
          }
          payload.chemical_selection = chemicalSelection;
          response = await savePlant(payload);
        }
        const resolvedPlantId = response?.plant_id || activePlantId;

        if (resolvedPlantId) {
          setCreatedPlantId(resolvedPlantId);
          updatePlantId(resolvedPlantId);

          let failedServices = [];

          await syncPendingPayload(
            PENDING_PLANT_CHEMICAL_LIBRARY_KEY,
            updatePlantChemicalLibrary,
            t("plantLevel.chemicalLibrary"),
            failedServices,
          );

          const utilitySyncHandledByConversion =
            await syncConvertedUtilityCostsIfNeeded(failedServices);

          if (!utilitySyncHandledByConversion) {
            await syncPendingPayload(
              PENDING_PLANT_UTILITY_COSTS_KEY,
              updatePlantUtilityCosts,
              t("plantLevel.utilityCosts"),
              failedServices,
            );
          }

          if (failedServices.length > 0 && key === "continue") {
            createNotification(
              ERROR_NOTIFICATION,
              t("addNewPlant.postCreateSyncFailed", {
                services: failedServices.join(t("valueStrings.and")),
              }),
              "",
            );
            return;
          }

          // Reset all changed flags after successful save
          setMandatoryComponentsStatus((prev) =>
            Object.fromEntries(
              Object.entries(prev).map(([key, val]) => [
                key,
                { ...val, changed: false },
              ]),
            ),
          );

          setBaselineUnits({
            currency,
            volume,
            conversion_rate: Number(conversion_rate) || 1,
          });

          if (key === "exit") {
            if (accessUserPlant) {
              dispatch({ type: "STORE_ACCESS_USER_PLANT", payload: false });
              navigate("/user-management");
            } else {
              navigate("/plants");
            }
            createNotification(
              SUCCESS_NOTIFICATION,
              t("addNewPlant.successfullySaved"),
              "",
            );
          } else if (!opts.skipNavigate) {
            let path = "";
            if (isEdit)
              path = `${basePath}analyseData?plantId=${resolvedPlantId}&isEdit=${isEdit}`;
            else path = `${basePath}analyseData?plantId=${resolvedPlantId}`;
            navigate(path);
          }
        } else {
          createNotification(ERROR_NOTIFICATION, response.detail.error, "");
        }
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      if (!opts.skipLoading) setIsLoading(false);
      setIsSaving(false);
    }
  };

  if (typeof window !== "undefined") {
    window.savePlantDetails = savePlantDetails;
  }

  const isStageConfigured = (stage = {}) => {
    const {
      num_pressure_vessels,
      num_element_per_pressure_vessel,
      is_isd,
      ro_nf_element_used,
    } = stage;
    const hasValidCounts =
      Number(num_pressure_vessels) > 0 &&
      Number(num_element_per_pressure_vessel) > 0;
    const hasValidElement = is_isd
      ? Array.isArray(ro_nf_element_used) && ro_nf_element_used.length > 0
      : typeof ro_nf_element_used === "string" &&
        ro_nf_element_used.trim() !== "";
    return hasValidCounts && hasValidElement;
  };

  const isTrainConfigured = (train = {}) =>
    train?.passes?.length > 0 &&
    train.passes.every(
      (pass) =>
        pass?.stages?.length > 0 && pass.stages.every(isStageConfigured),
    );

  const hasAtLeastOneConfiguredTrain = useMemo(
    () => Object.values(trains || {}).some(isTrainConfigured),
    [trains],
  );

  const hasMultipleTrains = useMemo(
    () => Object.values(trains || {}).length > 1,
    [trains],
  );

  const handleImportTrainConfigClick = () => {
    setIsTrainConfigModalOpen(true);
  };

  const handleTrainConfigModalClose = () => {
    setIsTrainConfigModalOpen(false);
  };

  const handleImportTrainConfig = (sourceTrainId, targetTrainIds) => {
    if (!sourceTrainId || !Array.isArray(targetTrainIds)) return;

    const sourceTrain = Object.values(trains || {}).find(
      (t) => t?.train_id === sourceTrainId,
    );
    if (!sourceTrain) return;

    const clonePasses = () =>
      JSON.parse(JSON.stringify(sourceTrain.passes || []));
    const updatedTrains = Object.values(trains || {}).map((train) =>
      targetTrainIds.includes(train.train_id)
        ? {
            ...train,
            is_dupont_element: sourceTrain.is_dupont_element,
            passes: clonePasses(),
          }
        : train,
    );

    setTrains({ ...updatedTrains });

    // Re-validate: check if all trains are now configured
    const allTrainsConfigured =
      Object.values(updatedTrains).every(isTrainConfigured);
    if (allTrainsConfigured) {
      setStageValidationSucessfull(true);
    }

    updateComponentStatus("roConfiguration", { changed: true });
  };

  const hasUnsavedChanges = useMemo(() => {
    // Don't block navigation during save operations
    if (isSaving) return false;

    return Object.values(mandatoryComponentsStatus).some(
      (status) => status.changed === true,
    );
  }, [mandatoryComponentsStatus, isSaving]);

  useUnsavedChangesBlocker(hasUnsavedChanges);

  return (
    <div className="container-main">
      <Header showPlantMenu isLoading={isLoading} />

      <div className="main-container">
        <div className="content-container descriptionText">
          {isLoading ? (
            <div className="circular-loader-overlay">
              <CircularLoader />
            </div>
          ) : (
            <div>
              <div className="calculatedValueText" id="tour-add-plant-intro">
                {t("roAdvisorProject.plantSetup")}
              </div>
              <div className="ro-card-wrapper position-relative">
                <div className="ro-card-header">
                  <div className="teal-heading ">
                    {t("plantInformationStrings.plantInfo")}
                  </div>
                </div>
                <div className="grid-container-row">
                  <InputBox
                    name={"installationName"}
                    value={plantDetail["installationName"]}
                    labelText={t(`addNewPlant.${"installationName"}`)}
                    placeholder={t(`addNewPlant.${"installationName"}`)}
                    rows={1}
                    isRequired={true}
                    onChange={(value) =>
                      handleInputChange("installationName", value)
                    }
                    errorMsg={plantDetailerror["installationName"]}
                    maxLength={250}
                  />
                  <InputBox
                    name={"city"}
                    value={plantDetail["city"]}
                    labelText={t(`addNewPlant.${"city"}`)}
                    placeholder={t(`addNewPlant.${"city"}`)}
                    rows={1}
                    isRequired={false}
                    onChange={(value) => handleInputChange("city", value)}
                    errorMsg={plantDetailerror["city"]}
                    maxLength={100}
                  />
                </div>
                <div className="grid-container-row marginTop5 marginBtm5">
                  <Dropdown
                    labelText={t("addNewPlant.country")}
                    placeholder={t("addNewPlant.country")}
                    value={country}
                    onChange={changeCountry}
                    options={countryOptions}
                    errorMsg={plantDetailerror["country"]}
                    isRequired
                    showCancelIcon
                  />
                  <Dropdown
                    labelText={t("addNewPlant.waterType")}
                    placeholder={t("addNewPlant.waterType")}
                    value={waterType}
                    onChange={changeWaterType}
                    options={waterTypeOptions}
                    errorMsg={plantDetailerror["waterType"]}
                    isRequired
                    showCancelIcon
                  />
                </div>

                <InputBox
                  name={"description"}
                  value={plantDetail["description"]}
                  labelText={t(`addNewPlant.${"description"}`)}
                  placeholder={t(`addNewPlant.${"description"}`)}
                  rows={3}
                  isRequired={false}
                  onChange={(value) => handleInputChange("description", value)}
                  errorMsg={plantDetailerror["description"]}
                  maxLength={500}
                />
              </div>
              <div>
                <div className="ro-card-wrapper position-relative">
                  <div className="ro-card-header">
                    <div className="info-section">
                      <div className="teal-heading">
                        {t("addNewPlant.reverseOsmosis")}
                      </div>
                      <IconWithTooltip
                        label={t("helpInformation.roConfigurationHelp")}
                      />

                      {!stageValidationSucessfull && (
                        <ErrorLabel
                          className="red-heading margin-left-ro"
                          message={t("addNewPlant.mandatoryRequired")}
                        />
                      )}
                    </div>
                    <WPTertiaryButton
                      id="import-train-config-btn"
                      label={t("importTrainConfig.title")}
                      icon={<NodeExtensionIcon />}
                      iconPlacement="before"
                      size="small"
                      className="import-train-config-btn"
                      disabled={
                        !hasAtLeastOneConfiguredTrain || !hasMultipleTrains
                      }
                      onClick={handleImportTrainConfigClick}
                    />
                  </div>

                  <div className="description-text ">
                    <RoConfiguration
                      trains={trains}
                      setTrains={setTrains}
                      setTotalNumberOfElements={setTotalNumberOfElements}
                      stageValidationSucessfull={stageValidationSucessfull}
                      setStageValidationSucessfull={
                        setStageValidationSucessfull
                      }
                      noOfTrains={noOfTrains}
                      RONFElements={RONFElements}
                      setMandatoryComponentsStatus={
                        setMandatoryComponentsStatus
                      }
                    />

                    <div className="flex-jc-end padding-top-20 align-items-center ">
                      {t("addNewPlant.totalNoOfElements")}
                      <div className="padding-left-50">
                        <InputBox
                          value={totalNumberOfElements}
                          placeholder={t(`addNewPlant.totalNoOfElements`)}
                          disabled
                          hideClearBtn
                        />
                      </div>
                    </div>
                  </div>
                  <CommentsOnSpecialElements
                    commentsData={selectedElementsCommentsData}
                  />
                </div>
                <Settings
                  disabled={false}
                  setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                />
                <div id="chemical-selection-fields">
                  <ChemicalsUsedForCleaning
                    chemicalsUsed={chemicalSelection}
                    onChemicalsChange={setChemicalSelection}
                    setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                  />
                </div>
                <div className=" button-right-container padding-top-20 ">
                  <DupontButton
                    customClass={"margin-bottom margin-left-10"}
                    onClick={() => savePlantDetails("exit")}
                  >
                    {t("ValueStrings.saveExit")}
                  </DupontButton>
                  <DupontButton
                    customClass={"margin-bottom margin-left-10"}
                    onClick={() => savePlantDetails("continue")}
                  >
                    {t("ValueStrings.saveContinue")}
                  </DupontButton>
                </div>
                {isTrainConfigModalOpen && (
                  <ImportTrainConfigModal
                    show={isTrainConfigModalOpen}
                    trains={trains}
                    onClose={handleTrainConfigModalClose}
                    onImport={handleImportTrainConfig}
                    isTrainConfigured={isTrainConfigured}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};
export default AddNewPlant;
